import React, { useEffect, useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import Loader from "./components/Loader";
import { saveMovie } from "./config/firebaseService";

const Home = () => {
  const [loading, setLoading] = useState(false);

  const [nowPlayingMovies, setNowPlayingMovies] = useState([]);
  const [topRatedMovies, setTopRatedMovies] = useState([]);
  const [upcomingMovies, setUpcomingMovies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [selectedTab, setSelectedTab] = useState(() => {
    const saved = sessionStorage.getItem("cb:home:selectedTab");
    return saved === "top" || saved === "now" || saved === "upcoming" ? saved : "top";
  }); // top | now | upcoming

  const top3SectionRef = useRef(null);

  // Sidebar is managed globally by Layout; Home renders only page content

  useEffect(() => {
    // Persist selected tab so we can restore it when navigating back from movie page
    try {
      sessionStorage.setItem("cb:home:selectedTab", selectedTab);
    } catch {}
  }, [selectedTab]);

  // Sidebar is managed globally by Layout; Home renders only page content

  useEffect(() => {
    const NOW_KEY = "cb:movies:now_playing";
    const UPCOMING_KEY = "cb:movies:upcoming";

    const cachedNow = sessionStorage.getItem(NOW_KEY);
    const cachedUpcoming = sessionStorage.getItem(UPCOMING_KEY);

    let pendingFetches = [];

    // Load cached if present
    if (cachedNow) {
      try { setNowPlayingMovies(JSON.parse(cachedNow)); } catch {}
    } else {
      pendingFetches.push("now_playing");
    }

    if (cachedUpcoming) {
      try { setUpcomingMovies(JSON.parse(cachedUpcoming)); } catch {}
    } else {
      pendingFetches.push("upcoming");
    }

    const fetchMoviesByCategory = async (category) => {
      try {
        const response = await fetch(`/api/movies/${category}`);
        const data = await response.json();
        // Set state and cache
        if (category === "now_playing") {
          setNowPlayingMovies(data);
          sessionStorage.setItem(NOW_KEY, JSON.stringify(data));
        } else if (category === "upcoming") {
          setUpcomingMovies(data);
          sessionStorage.setItem(UPCOMING_KEY, JSON.stringify(data));
        }

        // Save all movies to Firebase (first load only)
        if (Array.isArray(data)) {
          await Promise.all(
            data.map((m) => (m && m.id ? saveMovie(m.id, m) : null))
          );
        }
      } catch (error) {
        console.error(`Error fetching ${category} movies:`, error);
      }
    };

    if (pendingFetches.length === 0) {
      setIsLoading(false);
      return;
    }

    Promise.all(pendingFetches.map((c) => fetchMoviesByCategory(c))).then(() => setIsLoading(false));
  }, []);

  // On first visit in this session, auto-scroll to Top 3 section once content is ready
  useEffect(() => {
    if (isLoading) return;
    try {
      const alreadyHandled = sessionStorage.getItem("cb:home:firstVisitScrolled");
      if (!alreadyHandled) {
        // Ensure Top tab is active on first visit
        setSelectedTab("top");
        // Smooth scroll into view
        const el = top3SectionRef.current || document.getElementById("top3-section");
        if (el && typeof el.scrollIntoView === "function") {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        sessionStorage.setItem("cb:home:firstVisitScrolled", "1");
      }
    } catch {}
  }, [isLoading]);

  // Derive top rated from the now playing list (best vote_average) so it uses the same poster URLs
  useEffect(() => {
    if (!Array.isArray(nowPlayingMovies) || nowPlayingMovies.length === 0) {
      setTopRatedMovies([]);
      return;
    }

    const top = nowPlayingMovies
      .slice()
      .sort((a, b) => (b.vote_average || 0) - (a.vote_average || 0))
      .slice(0, 3);

    setTopRatedMovies(top);
  }, [nowPlayingMovies]);

  const handleLinkClick = () => {
    setLoading(true);
  };

  // Upcoming movies that are NOT in now playing (avoid duplicates across sections)
  const uniqueUpcoming = useMemo(() => {
    if (!Array.isArray(upcomingMovies) || !Array.isArray(nowPlayingMovies)) return [];
    const nowIds = new Set(nowPlayingMovies.map((m) => m.id));
    return upcomingMovies.filter((m) => !nowIds.has(m.id));
  }, [upcomingMovies, nowPlayingMovies]);

  // Choose a featured movie for the hero (prefer top rated -> now playing -> upcoming)
  const featuredMovie = useMemo(() => {
    const pickFirst = (arr) => (Array.isArray(arr) && arr.length > 0 ? arr[0] : null);
    if (selectedTab === "top") return pickFirst(topRatedMovies);
    if (selectedTab === "upcoming") return pickFirst(uniqueUpcoming) || pickFirst(upcomingMovies);
    if (selectedTab === "now") return pickFirst(nowPlayingMovies);
    // fallback
    return pickFirst(topRatedMovies) || pickFirst(nowPlayingMovies) || pickFirst(uniqueUpcoming) || pickFirst(upcomingMovies);
  }, [selectedTab, topRatedMovies, nowPlayingMovies, upcomingMovies, uniqueUpcoming]);

  const yearOf = (dateStr) => {
    if (!dateStr) return "";
    const y = new Date(dateStr).getFullYear();
    return Number.isNaN(y) ? "" : y;
  };

  const truncate = (str, max = 220) => {
    if (!str) return "";
    return str.length > max ? `${str.slice(0, max - 1)}…` : str;
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <main id="main-content">
            {/* Hero */}
            {featuredMovie && (
              <section className="hero">
                <figure className="hero-media">
                  <img
                    src={featuredMovie.backdrop_path || featuredMovie.poster_path}
                    alt={featuredMovie.title}
                    className="hero-image"
                  />
                  <figcaption className="visually-hidden">{featuredMovie.title} artwork</figcaption>
                </figure>
                <article className="hero-info">
                  <strong className="brand-pill">clip.board</strong>
                  <h1 className="hero-title">{featuredMovie.title}</h1>
                  <p className="hero-meta">
                    <time dateTime={featuredMovie.release_date}>
                      {yearOf(featuredMovie.release_date)}
                    </time>
                    {" "}·{" "}
                    <strong aria-label="Average rating">
                      {featuredMovie.vote_average ? featuredMovie.vote_average.toFixed(1) : "NR"}★
                    </strong>
                  </p>
                  <p className="hero-overview">{truncate(featuredMovie.overview)}</p>
                  <footer className="hero-actions">
                    {selectedTab === "upcoming" ? (
                      <Link to={`/movie/${featuredMovie.id}`} className="btn primary" onClick={handleLinkClick}>Preview</Link>
                    ) : (
                      <Link to={`/movie/${featuredMovie.id}`} className="btn primary">Book now</Link>
                    )}
                  </footer>
                </article>
              </section>
            )}

            {/* Chips */}
            <nav className="chip-row" aria-label="Browse categories">
              <button
                type="button"
                className={`chip ${selectedTab === "top" ? "active" : ""}`}
                aria-pressed={selectedTab === "top"}
                onClick={() => setSelectedTab("top")}
              >
                Top 3
              </button>
              <button
                type="button"
                className={`chip ${selectedTab === "now" ? "active" : ""}`}
                aria-pressed={selectedTab === "now"}
                onClick={() => setSelectedTab("now")}
              >
                Now Playing
              </button>
              <button
                type="button"
                className={`chip ${selectedTab === "upcoming" ? "active" : ""}`}
                aria-pressed={selectedTab === "upcoming"}
                onClick={() => setSelectedTab("upcoming")}
              >
                Upcoming
              </button>
            </nav>

            {/* Single row depending on selection */}
            <section className="row-section" id="top3-section" ref={top3SectionRef}>
              <header className="row-header">
                <h2>
                  {selectedTab === "now" && "Our Now Playing Movies"}
                  {selectedTab === "top" && "Our Top 3 Movies"}
                  {selectedTab === "upcoming" && "Our Coming Soon Section"}
                </h2>
              </header>
              <ul className="carousel">
                {isLoading ? (
                  <Loader />
                ) : (() => {
                  let list = [];
                  if (selectedTab === "top") {
                    list = topRatedMovies;
                  } else if (selectedTab === "upcoming") {
                    list = (uniqueUpcoming.length >= 5 ? uniqueUpcoming.slice(0, 5) : upcomingMovies.slice(0, 5));
                  } else {
                    list = Array.isArray(nowPlayingMovies) ? nowPlayingMovies.slice(0, 10) : [];
                  }
                  if (!list || list.length === 0) return <li className="empty"><p>No movies found.</p></li>;
                  return list.map((movie) => (
                    <li key={movie.id} className="card">
                      <Link to={`/movie/${movie.id}`} className="card" onClick={handleLinkClick}>
                        <figure className="card-figure">
                          <img src={movie.poster_path} alt={`${movie.title} poster`} className="card-poster" />
                          <figcaption className="card-label">{movie.title}</figcaption>
                        </figure>
                      </Link>
                    </li>
                  ));
                })()}
              </ul>
            </section>
        </main>
      )}
    </>
  );
};

export default Home;
